import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { FaDownload } from "react-icons/fa";
import { Award, Clock, ShieldCheck, Target } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "../../../../axios";
import { errorToast } from "../../../../utils/toast";
import { useUser } from "../../../../hooks/use-user";
import Loding from "../../../../componant/Loader/Loding";

const ContestResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const contestIdFromState = location.state?.contestId;
  const { data: user } = useUser();

  const [countData, setCountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [hasContestEnded, setHasContestEnded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!contestIdFromState) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [detailsRes, questionsRes] = await Promise.all([
          axios.get("contestDetails", {
            params: { contestId: contestIdFromState },
          }),
          axios.get(`getContestQuestionsByContestId/${contestIdFromState}`),
        ]);

        const details = detailsRes?.data?.result;
        if (details) {
          setHasContestEnded(details.hasContestEnded || false);
          setCountData({
            rightCount: details.totalRightAnswerCount,
            wrongCount: details.totalWrongAnswerCount,
            totalAttempts: details.totalAttempts || 0,
            accuracy: details.accuracy || 0,
            totalTimeTaken: details.totalTimeTaken || 0,
            name: details.name,
            contestId: details.contestId,
            contestName: details.contestName,
            totalContestSlot: details.totalContestSlot,
            userRank: details.hasContestEnded ? details.userRank : null,
            winningAmount: details.hasContestEnded ? details.winningAmount : 0,
            totalUserContestJoinedCount: details.totalUserContestJoinedCount,
          });
        }

        const questions = questionsRes?.data?.result?.questions;
        if (Array.isArray(questions)) {
          setTotalQuestions(questions.length);
        }
      } catch (err) {
        errorToast(err?.response?.data?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, contestIdFromState]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e1b4b] to-[#0f172a] flex items-center justify-center">
        <Loding />
      </div>
    );
  }

  if (!contestIdFromState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e1b4b] to-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">No contest data found</p>
          <button
            onClick={() => navigate("/dash/contest")}
            className="px-6 py-2 bg-purple-600 text-white rounded-xl"
          >
            Go to Contests
          </button>
        </div>
      </div>
    );
  }

  const formatDateTime = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const downloadPDF = () => {
    const pdf = new jsPDF("p", "mm", "a4");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("LEARN2EARN24X7", 105, 20, { align: "center" });

    pdf.setFontSize(14);
    pdf.text("Performance Scorecard", 105, 30, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);

    let y = 50;

    pdf.text(`User: ${user?.name || "User"}`, 20, y);
    y += 10;

    pdf.text(`Contest: ${countData?.contestName || "-"}`, 20, y);
    y += 10;

    pdf.text(`Total Questions: ${totalQuestions}`, 20, y);
    y += 10;

    pdf.text(`Correct Answers: ${countData?.rightCount || 0}`, 20, y);
    y += 10;

    pdf.text(`Incorrect Answers: ${countData?.wrongCount || 0}`, 20, y);
    y += 10;

    pdf.text(`Accuracy: ${countData?.accuracy || 0}%`, 20, y);
    y += 10;

    pdf.text(`Rank: ${hasContestEnded ? (countData?.userRank || "-") : "Pending"}`, 20, y);
    y += 10;

    pdf.text(`Coins Earned: ${hasContestEnded ? (countData?.winningAmount || 0) : "Pending"}`, 20, y);

    pdf.save("Contest_Result.pdf");
  };

  const rightCount = countData?.rightCount || 0;
  const wrongCount = countData?.wrongCount || 0;
  const accuracy = countData?.accuracy || 0;
  const winningAmount = countData?.winningAmount || 0;
  const userRank = countData?.userRank;
  const totalTimeTaken = countData?.totalTimeTaken || 0;

  return (
    <div className="min-h-screen overflow-hidden flex flex-col bg-gradient-to-br from-[#1e1b4b] to-[#0f172a] justify-center items-center px-4 py-10 pt-20">
      <div
        id="result-pdf"
        className="bg-[#f8f6fb] rounded-3xl shadow-2xl w-full max-w-md p-6 relative"
      >
        {/* RANK BADGE */}
        {hasContestEnded && userRank && (
          <div className="absolute top-4 right-4 bg-yellow-400 text-white text-xs px-3 py-1 rounded-full shadow-md">
            🏆 Rank #{userRank}
          </div>
        )}
        {!hasContestEnded && (
          <div className="absolute top-4 right-4 bg-purple-500 text-white text-xs px-3 py-1 rounded-full shadow-md">
            ⏳ Rank Pending
          </div>
        )}

        {/* SHIELD ICON */}
        <div className="flex justify-center mb-4">
          <div className="bg-purple-200 p-4 rounded-full">
            <ShieldCheck className="text-purple-700" size={30} />
          </div>
        </div>

        {/* TITLE */}
        <div className="text-center mb-6">
          <h2 className="text-xs tracking-widest text-gray-500">
            LEARN2EARN24X7
          </h2>
          <h1 className="text-2xl font-bold text-gray-800">
            Performance Scorecard
          </h1>
          {countData?.contestName && (
            <p className="text-sm text-gray-500 mt-1">{countData.contestName}</p>
          )}
          <div className="w-16 h-1 bg-purple-600 mx-auto mt-2 rounded-full"></div>
        </div>

        {/* USER INFO */}
        <div className="bg-gray-200 rounded-2xl p-4 flex items-center gap-6 mb-6">
          <img
            src={`https://ui-avatars.com/api/?name=${user?.name}&background=6d28d9&color=fff&size=128`}
            alt="avatar"
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <h3 className="font-bold text-gray-800">
              {user?.name || "User Name"}
            </h3>
          </div>
        </div>

        {/* MAIN SCORE */}
        <div className="text-center mb-6">
          <h1 className="text-5xl font-extrabold text-gray-800">
            {rightCount}
            <span className="text-lg text-gray-500">
              /{totalQuestions}
            </span>
          </h1>
          <p className="text-gray-500 mt-1">Correct Answers</p>
        </div>

        {/* STATS */}
        <div className="space-y-3 text-sm">
          <div className="flex justify-between border-b pb-2">
            <span>Total Questions</span>
            <span className="font-semibold">{totalQuestions}</span>
          </div>

          <div className="flex justify-between border-b pb-2">
            <span>Attempted</span>
            <span className="font-semibold">
              {rightCount + wrongCount}
            </span>
          </div>

          <div className="flex justify-between border-b pb-2">
            <span>Correct Answers</span>
            <span className="font-semibold text-green-600">
              {rightCount}
            </span>
          </div>

          <div className="flex justify-between">
            <span>Incorrect Answers</span>
            <span className="font-semibold text-red-500">
              {wrongCount}
            </span>
          </div>
        </div>

        {/* PERCENTILE BOX */}
        <div className="mt-6 bg-purple-200 rounded-2xl p-5 text-center">
          <p className="text-xs tracking-widest text-gray-600">
            ACCURACY
          </p>
          <h2 className="text-4xl font-extrabold text-purple-800">
            {accuracy}%
          </h2>
        </div>

        {/* EXTRA INFO */}
        <div className="mt-5 grid grid-cols-3 gap-3 text-xs text-gray-600">
          <div className="text-center">
            <Target className="mx-auto text-green-600 mb-1" size={18} />
            <p className="font-bold text-green-600">{accuracy}%</p>
            <p>Accuracy</p>
          </div>

          <div className="text-center">
            <Clock className="mx-auto text-purple-600 mb-1" size={18} />
            <p className="font-bold">{totalTimeTaken}s</p>
            <p>Time Taken</p>
          </div>

          <div className="text-center">
            <Award className="mx-auto text-orange-500 mb-1" size={18} />
            <p className="font-bold">{hasContestEnded ? `#${userRank || "-"}` : "Pending"}</p>
            <p>Rank</p>
          </div>
        </div>

        {/* TOTAL EARNED */}
        <div className="mt-6 bg-yellow-50 rounded-xl p-4 flex justify-between items-center">
          <span className="font-semibold text-gray-700">Total Earned</span>
          {hasContestEnded ? (
            <span className="text-lg font-bold text-yellow-600">
              {winningAmount} Coins
            </span>
          ) : (
            <span className="text-lg font-bold text-purple-600">
              Pending
            </span>
          )}
        </div>

        {/* BUTTONS */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={downloadPDF}
            className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg hover:opacity-90 transition"
          >
            <FaDownload className="inline mr-2" />
            Download PDF
          </button>

          <button
            onClick={() => navigate("/dash/contest")}
            className="flex-1 bg-yellow-400 py-3 rounded-xl font-bold hover:bg-yellow-500 transition"
          >
            Play New Contest
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContestResultPage;
